import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { FaEye, FaFileExcel, FaFilePdf, FaPrint, FaFilter, FaTrash, FaChartBar, FaExchangeAlt, FaTimes } from 'react-icons/fa';
import { getAuditLogs, exportAuditLogsToExcel, exportAuditLogsToPDF, cleanupAuditLogs, getAuditLogDetails } from '../../../redux/Slices/AuditLogSlice';
import { getUsers } from '../../../redux/Slices/UsersSlice';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../shared/hooks/TitleContext';
import i18n from '../../../i18n/i18n';
import axios from 'axios';
import { BASE_URL, getToken, getCurrentLanguage, getPdfRowStyles, chunkArray } from '../../../shared/utils.js/utils';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// PDF layout constants — derived from page dimensions, not magic numbers
const PDF_PAGE_TOTAL_HEIGHT = 1123;
const PDF_OUTER_PADDING = 80;
const PDF_BORDER_INNER_PADDING = 46;
const PDF_HEADER_AREA = 86;
const PDF_FOOTER_CLEARANCE = 130;
const PDF_TITLE_DATE_HEIGHT = 54;
const PDF_TABLE_HEADER_HEIGHT = 30;
const PDF_ROW_HEIGHT = 28;
const AUDIT_LOGS_PDF_ITEMS_PER_PAGE = Math.max(5, Math.floor(
  (PDF_PAGE_TOTAL_HEIGHT - PDF_OUTER_PADDING - PDF_BORDER_INNER_PADDING -
   PDF_HEADER_AREA - PDF_FOOTER_CLEARANCE - PDF_TITLE_DATE_HEIGHT - PDF_TABLE_HEADER_HEIGHT) /
  PDF_ROW_HEIGHT
));

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
    // Some APIs return pagination fields (current_page, last_page, per_page, total) alongside data
    if (!pagination && payload.data?.current_page !== undefined) {
      pagination = {
        current_page: payload.data.current_page || 1,
        last_page: payload.data.last_page || 1,
        per_page: payload.data.per_page || 10,
        total: payload.data.total || items.length,
      };
    }
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};


const paginationButtonStyle = (disabled) => ({
  padding: '8px 16px',
  backgroundColor: disabled ? '#333' : '#AC2000',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const applySheetDirection = (worksheet, rtl = false) => {
  if (!worksheet || !worksheet['!ref']) return;
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  for (let R = range.s.r; R <= range.e.r; R += 1) {
    for (let C = range.s.c; C <= range.e.c; C += 1) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (!cell) continue;
      const alignment = rtl ? 'right' : 'left';
      cell.s = {
        ...(cell.s || {}),
        alignment: { ...(cell.s?.alignment || {}), horizontal: alignment },
      };
    }
  }
};

const writeWorkbook = (workbook, filename) => {
  XLSX.writeFile(workbook, filename, { cellStyles: true });
};

const AuditLogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const user = useSelector((state) => state.auth.user);
  const getUserData = () => {
    if (user) return user;
    const storedUser = localStorage.getItem("useralbaraqawy");
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  };

  const userData = getUserData();
  const getUserPermissions = () => {
    if (Array.isArray(userData?.data?.permissions)) return userData.data.permissions;
    if (Array.isArray(userData?.permissions)) return userData.permissions;
    if (Array.isArray(userData?.data?.data?.permissions)) return userData.data.data.permissions;
    return [];
  };

  const getUserRoles = () => {
    if (Array.isArray(userData?.data?.roles)) return userData.data.roles;
    if (Array.isArray(userData?.roles)) return userData.roles;
    if (Array.isArray(userData?.data?.data?.roles)) return userData.data.data.roles;
    return [];
  };

  const userPermissions = getUserPermissions();
  const userRoles = getUserRoles();

  // Check if user has cleanup permission or is admin
  const hasCleanupPermission = () => {
    const isAdmin = userRoles.some(role => {
      const roleName = typeof role === 'string' ? role : role?.name;
      return roleName === 'admin' || roleName === 'Admin' || roleName === 'administrator' || roleName === 'Administrator';
    });
    if (isAdmin) return true;
    return userPermissions.includes('audit_logs.cleanup');
  };

  const canCleanup = hasCleanupPermission();

  const [auditLogs, setAuditLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const [previewPage, setPreviewPage] = useState(1); // For preview pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [userId, setUserId] = useState(searchParams.get('user_id') || '');
  const [modelType, setModelType] = useState(searchParams.get('model_type') || '');
  const [modelId, setModelId] = useState(searchParams.get('model_id') || '');
  const [event, setEvent] = useState(searchParams.get('event') || '');
  const [fromDate, setFromDate] = useState(searchParams.get('from_date') || '');
  const [toDate, setToDate] = useState(searchParams.get('to_date') || '');
  const [perPage, setPerPage] = useState(searchParams.get('per_page') || '15');

  // User dropdown
  const [users, setUsers] = useState([]);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userPagination, setUserPagination] = useState(null);
  const userSearchInputRef = useRef(null);

  const [showFilters, setShowFilters] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupOlderThanDays, setCleanupOlderThanDays] = useState(90);
  const [isCleaning, setIsCleaning] = useState(false);
  const [previewLog, setPreviewLog] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [pdfType, setPdfType] = useState(null); // 'all' or null
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const pageNumbers = useMemo(() => {
    if (!pagination) return [];
    const current = pagination.current_page || 1;
    const last = pagination.last_page || 1;
    const show = 5;
    let start = Math.max(1, current - Math.floor(show / 2));
    let end = Math.min(last, start + show - 1);
    if (end - start < show - 1) start = Math.max(1, end - show + 1);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [pagination]);



  useEffect(() => {
    setTitle('auditLogs.title', { defaultValue: 'تحركات المستخدمين' });
  }, [setTitle]);

  const fetchAuditLogs = async (page = currentPage) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        per_page: perPage || 15,
      };
      if (userId) params.user_id = userId;
      if (modelType) params.model_type = modelType;
      if (modelId) params.model_id = modelId;
      if (event) params.event = event;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const result = await dispatch(getAuditLogs(params));
      if (getAuditLogs.fulfilled.match(result)) {
        const { items, pagination: paginationData } = normalizeListResponse(result.payload);
        setAuditLogs(items);
        setPagination(paginationData);
      } else {
        toast.error(result.payload || t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجلات' }), { rtl: isRTL });
      }
    } catch (err) {
      toast.error(t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجلات' }), { rtl: isRTL });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async (page = userPage, search = userSearchTerm) => {
    try {
      const params = { page, per_page: 10 };
      if (search) params.search = search;
      const result = await dispatch(getUsers(params));
      if (getUsers.fulfilled.match(result)) {
        const response = result.payload;
        const data = response?.data || response;
        const usersList = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        setUsers(usersList);
        const paginationData = data?.meta || data?.pagination || null;
        setUserPagination(paginationData);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage]);

  useEffect(() => {
    if (userDropdownOpen) {
      fetchUsers(userPage, userSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDropdownOpen, userPage]);

  useEffect(() => {
    if (userSearchTerm !== undefined && userDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setUserPage(1);
        fetchUsers(1, userSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearchTerm, userDropdownOpen]);

  // Handle mouse move for dragging preview
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      e.preventDefault();
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { passive: false });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    if (modelType) params.append('model_type', modelType);
    if (modelId) params.append('model_id', modelId);
    if (event) params.append('event', event);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (perPage) params.append('per_page', perPage);
    setSearchParams(params);
    fetchAuditLogs(1);
  };

  const handleResetFilters = () => {
    setUserId('');
    setModelType('');
    setModelId('');
    setEvent('');
    setFromDate('');
    setToDate('');
    setPerPage('15');
    setSearchParams({});
    setCurrentPage(1);
    fetchAuditLogs(1);
  };

  const handleExportExcel = async () => {
    try {
      const params = {};
      if (userId) params.user_id = userId;
      if (modelType) params.model_type = modelType;
      if (modelId) params.model_id = modelId;
      if (event) params.event = event;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const blob = await exportAuditLogsToExcel(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('auditLogs.success.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('auditLogs.errors.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = {};
      if (userId) params.user_id = userId;
      if (modelType) params.model_type = modelType;
      if (modelId) params.model_id = modelId;
      if (event) params.event = event;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const blob = await exportAuditLogsToPDF(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('auditLogs.success.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('auditLogs.errors.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const handleExportExcelSingle = async (log) => {
    try {
      const worksheet = XLSX.utils.json_to_sheet([{
        'ID': log.id,
        [t('auditLogs.user', { defaultValue: 'المستخدم' })]: log.user?.name || log.user?.email || (log.user_id ? `User #${log.user_id}` : '—'),
        [t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' })]: log.auditable_type || log.model_type || '—',
        [t('auditLogs.auditableId', { defaultValue: 'معرف السجل' })]: log.auditable_id || log.model_id || '—',
        [t('auditLogs.event', { defaultValue: 'الحدث' })]: log.event || '—',
        [t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' })]: log.ip_address || '—',
        [t('auditLogs.userAgent', { defaultValue: 'المتصفح' })]: log.user_agent || '—',
        [t('auditLogs.url', { defaultValue: 'الرابط' })]: log.url || '—',
        [t('auditLogs.tags', { defaultValue: 'العلامات' })]: log.tags ? (Array.isArray(log.tags) ? log.tags.join(', ') : String(log.tags)) : '—',
        [t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })]: formatDate(log.created_at)
      }]);
      applySheetDirection(worksheet, isRTL);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Log');
      writeWorkbook(workbook, `audit-log-${log.id}-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('auditLogs.success.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('auditLogs.errors.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const handleExportPDFSingle = async (log) => {
    try {
      const result = await dispatch(getAuditLogDetails({ id: log.id, language: i18n.language }));
      if (getAuditLogDetails.fulfilled.match(result)) {
        const logData = result.payload?.data || result.payload;
        setPreviewLog(logData);
        setPdfType('single');
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }
        await new Promise((resolve) => setTimeout(resolve, 200));
        setIsGeneratingPdf(true);

        // Get all pages (main page + continuation pages for old values + continuation pages for new values)
        const oldValuesEntries = Object.entries(logData.old_values || {});
        const newValuesEntries = Object.entries(logData.new_values || {});
        const firstPageOldItems = 4;
        const firstPageNewItems = 5;
        const continuationPageItems = 10;
        
        const elements = [];
        
        // First page
        elements.push(document.getElementById(`audit-log-pdf-single-${log.id}`));
        
        // Calculate continuation pages for old values
        let currentPageIndex = 1;
        if (oldValuesEntries.length > firstPageOldItems) {
          const remainingItems = oldValuesEntries.length - firstPageOldItems;
          const continuationPages = Math.ceil(remainingItems / continuationPageItems);
          for (let i = 0; i < continuationPages; i++) {
            const pageEl = document.getElementById(`audit-log-pdf-single-${log.id}-page-${currentPageIndex + i}`);
            if (pageEl) elements.push(pageEl);
          }
          currentPageIndex += continuationPages;
        }
        
        // Calculate continuation pages for new values
        if (newValuesEntries.length > firstPageNewItems) {
          const remainingItems = newValuesEntries.length - firstPageNewItems;
          const continuationPages = Math.ceil(remainingItems / continuationPageItems);
          for (let i = 0; i < continuationPages; i++) {
            const pageEl = document.getElementById(`audit-log-pdf-single-${log.id}-page-${currentPageIndex + i}`);
            if (pageEl) elements.push(pageEl);
          }
        }
        
        const validElements = elements.filter(Boolean);
        if (validElements.length === 0) {
          toast.error(t('auditLogs.errors.printError', { defaultValue: 'قالب الطباعة غير متوفر' }), { rtl: isRTL });
          return;
        }

        const devicePixelRatio = window.devicePixelRatio || 1;
        const scale = Math.min(2, Math.max(1.5, devicePixelRatio));
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        for (let pageIdx = 0; pageIdx < validElements.length; pageIdx++) {
          if (pageIdx > 0) pdf.addPage();

          const canvas = await html2canvas(validElements[pageIdx], {
            scale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            letterRendering: false,
            allowTaint: false,
            removeContainer: false,
            imageTimeout: 10000,
            onclone: (clonedDoc) => {
              const clonedElement = clonedDoc.querySelector('div[aria-hidden="true"]');
              if (clonedElement) {
                clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                clonedElement.style.fontWeight = 'bold';
                clonedElement.style.direction = dir;
              }
            }
          });

          const imgData = canvas.toDataURL('image/png', 1.0);
          const imgHeight = (canvas.height * pdfWidth) / canvas.width;
          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
        }

        pdf.save(`ACE_AuditLog_${log.id}_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success(t('auditLogs.success.printed', { defaultValue: 'تم طباعة السجل بنجاح' }), { rtl: isRTL });
        setIsGeneratingPdf(false);
        setPdfType(null);
        setPreviewLog(null);
      } else {
        toast.error(result.payload || t('auditLogs.errors.printError', { defaultValue: 'فشل في طباعة السجل' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(t('auditLogs.errors.printError', { defaultValue: 'فشل في طباعة السجل' }), { rtl: isRTL });
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPreviewLog(null);
    }
  };

  const handlePreviewPDF = async (log) => {
    try {
      const result = await dispatch(getAuditLogDetails({ id: log.id, language: i18n.language }));
      if (getAuditLogDetails.fulfilled.match(result)) {
        const logData = result.payload?.data || result.payload;
        setPreviewLog(logData);
        setIsPreviewOpen(true);
        setPreviewZoom(1.0);
        setPreviewPosition({ x: 0, y: 0 });
      } else {
        toast.error(result.payload || t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجل' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجل' }), { rtl: isRTL });
    }
  };

  const handlePreviewAll = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: 1,
        per_page: pagination?.total || 10000,
      };
      if (userId) params.user_id = userId;
      if (modelType) params.model_type = modelType;
      if (modelId) params.model_id = modelId;
      if (event) params.event = event;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const result = await dispatch(getAuditLogs(params));
      if (getAuditLogs.fulfilled.match(result)) {
        const { items } = normalizeListResponse(result.payload);
        if (!items || items.length === 0) {
          toast.error(t('auditLogs.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
          return;
        }
        const chunks = chunkArray(items, AUDIT_LOGS_PDF_ITEMS_PER_PAGE);
        setPreviewLog({ all: true, logs: items, allLogs: items, chunks: chunks, filters: { userId, modelType, modelId, event, fromDate, toDate } });
        setIsPreviewOpen(true);
        setPreviewZoom(1.0);
        setPreviewPosition({ x: 0, y: 0 });
      } else {
        toast.error(result.payload || t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجلات' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجلات' }), { rtl: isRTL });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintAll = async () => {
    let originalLogs = null;
    try {
      setIsLoading(true);
      setIsGeneratingPdf(true);

      const params = {
        page: 1,
        per_page: pagination?.total || 10000,
      };
      if (userId) params.user_id = userId;
      if (modelType) params.model_type = modelType;
      if (modelId) params.model_id = modelId;
      if (event) params.event = event;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const result = await dispatch(getAuditLogs(params));
      if (getAuditLogs.fulfilled.match(result)) {
        const { items } = normalizeListResponse(result.payload);
        if (!items || items.length === 0) {
          toast.error(t('auditLogs.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
          setIsGeneratingPdf(false);
          setIsLoading(false);
          return;
        }

        // Temporarily set all logs for PDF generation
        originalLogs = auditLogs;

        const chunks = chunkArray(items, AUDIT_LOGS_PDF_ITEMS_PER_PAGE);
        setPdfChunks(chunks);
        setAuditLogs(chunks[0] || []); // Set first chunk for initial render

        setPdfType('all');

        // Wait for React to render all chunks - use multiple frames to ensure DOM is ready
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => requestAnimationFrame(resolve));

        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }

        // Wait for all PDF elements to be rendered in the DOM
        const waitForElements = (maxAttempts = 50) => {
          return new Promise((resolve, reject) => {
            let attempts = 0;
            const checkElements = () => {
              attempts++;
              const allElementsExist = chunks.every((_, index) => {
                return document.getElementById(`audit-logs-pdf-all-${index}`) !== null;
              });

              if (allElementsExist) {
                resolve();
              } else if (attempts >= maxAttempts) {
                // If not all elements exist after max attempts, proceed anyway
                console.warn('Some PDF elements may not be fully rendered');
                resolve();
              } else {
                requestAnimationFrame(checkElements);
              }
            };
            checkElements();
          });
        };

        await waitForElements();

        // Additional small delay to ensure images are loaded
        await new Promise((resolve) => setTimeout(resolve, 500));

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const devicePixelRatio = window.devicePixelRatio || 1;
        const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

        // Generate PDF for each chunk
        for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
          const element = document.getElementById(`audit-logs-pdf-all-${chunkIndex}`);
          if (!element) {
            if (chunkIndex === 0) {
              setAuditLogs(originalLogs);
              setPdfChunks([]);
              toast.error(t('auditLogs.errors.printError', { defaultValue: 'قالب الطباعة غير متوفر' }), { rtl: isRTL });
              setIsGeneratingPdf(false);
              setIsLoading(false);
              return;
            }
            console.warn(`Chunk ${chunkIndex} element not found, skipping`);
            continue;
          }

          // Wait for images in this chunk to load
          const images = element.querySelectorAll('img');
          await Promise.all(Array.from(images).map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = resolve; // Continue even if image fails
              setTimeout(resolve, 2000); // Timeout after 2 seconds
            });
          }));

          const canvas = await html2canvas(element, {
            scale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            letterRendering: false,
            allowTaint: false,
            removeContainer: false,
            imageTimeout: 15000,
            onclone: (clonedDoc) => {
              const clonedElement = clonedDoc.getElementById(`audit-logs-pdf-all-${chunkIndex}`);
              if (clonedElement) {
                clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                clonedElement.style.fontWeight = 'bold';
                clonedElement.style.direction = dir;
              }
            }
          });

          const imgData = canvas.toDataURL('image/png', 1.0);
          const imgHeight = (canvas.height * pdfWidth) / canvas.width;

          if (chunkIndex > 0) {
            pdf.addPage();
          }

          // Add the image to the current page. Since each chunk has a fixed height (1123px),
          // it should align perfectly with the PDF page height.
          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight, undefined, 'FAST');
        }

        pdf.save(`ACE_AuditLogs_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success(t('auditLogs.success.printed', { defaultValue: 'تم طباعة السجلات بنجاح' }), { rtl: isRTL });

        // Restore original logs
        setAuditLogs(originalLogs);
        setPdfChunks([]);
      } else {
        toast.error(result.payload || t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجلات' }), { rtl: isRTL });
      }
    } catch (error) {
      console.error('Audit Logs PDF error:', error);
      toast.error(t('auditLogs.errors.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
      // Restore original logs on error
      if (originalLogs) {
        setAuditLogs(originalLogs);
      }
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setIsLoading(false);
    }
  };

  const handleExportAllToExcel = async () => {
    try {
      const params = {
        page: 1,
        per_page: 10000,
      };
      if (userId) params.user_id = userId;
      if (modelType) params.model_type = modelType;
      if (modelId) params.model_id = modelId;
      if (event) params.event = event;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const result = await dispatch(getAuditLogs(params));
      if (getAuditLogs.fulfilled.match(result)) {
        const { items } = normalizeListResponse(result.payload);

        const worksheet = XLSX.utils.json_to_sheet(items.map(log => ({
          'ID': log.id,
          [t('auditLogs.user', { defaultValue: 'المستخدم' })]: log.user?.name || log.user?.email || (log.user_id ? `User #${log.user_id}` : '—'),
          [t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' })]: getModelTypeLabel(log.auditable_type || log.model_type),
          [t('auditLogs.auditableId', { defaultValue: 'معرف السجل' })]: log.auditable_id || log.model_id || '—',
          [t('auditLogs.event', { defaultValue: 'الحدث' })]: log.event || '—',
          [t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' })]: log.ip_address || '—',
          [t('auditLogs.userAgent', { defaultValue: 'المتصفح' })]: log.user_agent || '—',
          [t('auditLogs.url', { defaultValue: 'الرابط' })]: log.url || '—',
          [t('auditLogs.tags', { defaultValue: 'العلامات' })]: log.tags ? (Array.isArray(log.tags) ? log.tags.join(', ') : String(log.tags)) : '—',
          [t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })]: formatDate(log.created_at)
        })));
        applySheetDirection(worksheet, isRTL);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Logs');
        writeWorkbook(workbook, `audit-logs-all-${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success(t('auditLogs.success.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
      } else {
        toast.error(result.payload || t('auditLogs.errors.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error.message || t('auditLogs.errors.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getEventLabel = (eventType) => {
    const labels = {
      created: t('auditLogs.created'),
      updated: t('auditLogs.updated'),
      deleted: t('auditLogs.deleted'),
      restored: t('auditLogs.restored'),
    };
    return labels[eventType] || eventType;
  };

  const getModelTypeLabel = (modelType) => {
    if (!modelType) return '—';
    const modelName = modelType.replace(/App\\Models\\/g, '');
    const translationKey = `auditLogs.models.${modelName}`;
    const translated = t(translationKey, { defaultValue: modelName });
    return translated !== translationKey ? translated : modelName;
  };

  const translateFieldKey = (key) => {
    if (!key) return key;
    const keyLower = key.toLowerCase();
    if (keyLower === 'is_active' || keyLower === 'is active') {
      return t('auditLogs.isActive');
    }
    // يمكن إضافة ترجمات أخرى للمفاتيح هنا
    return key;
  };

  const formatJson = (obj) => {
    if (!obj) return '—';
    if (typeof obj === 'string') {
      try {
        return JSON.stringify(JSON.parse(obj), null, 2);
      } catch {
        return obj;
      }
    }
    return JSON.stringify(obj, null, 2);
  };

  const getSelectedUser = () => {
    if (!userId) return t('auditLogs.selectUser', { defaultValue: 'اختر المستخدم' });
    const selected = users.find((u) => String(u.id) === String(userId));
    return selected ? (selected.name || selected.email || `User #${userId}`) : `User #${userId}`;
  };

  const modelTypes = [
    { value: 'App\\Models\\Customer', label: t('auditLogs.customer') },
    { value: 'App\\Models\\Vendor', label: t('auditLogs.vendor') },
    { value: 'App\\Models\\Contract', label: t('auditLogs.contract') },
    { value: 'App\\Models\\Project', label: t('auditLogs.project') },
    { value: 'App\\Models\\User', label: t('auditLogs.user') },
  ];

  const eventTypes = [
    { value: 'created', label: t('auditLogs.created') },
    { value: 'updated', label: t('auditLogs.updated') },
    { value: 'deleted', label: t('auditLogs.deleted') },
    { value: 'restored', label: t('auditLogs.restored') },
  ];

  return (
    <div style={{ padding: '20px', direction: dir, textAlign }}>
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ color: 'white', margin: 0, textAlign }}>
            {t('auditLogs.title', { defaultValue: 'تحركات المستخدمين' })}
          </h2>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              backgroundColor: showFilters ? '#AC2000' : '#444',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaFilter /> {t('auditLogs.filters', { defaultValue: 'الفلاتر' })}
          </button>
          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: '#444',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaEye /> {t('auditLogs.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isGeneratingPdf ? 0.6 : 1
            }}
          >
            <FaPrint /> {isGeneratingPdf ? t('auditLogs.generating', { defaultValue: 'جاري التوليد...' }) : t('auditLogs.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaFileExcel /> {t('auditLogs.exportExcel', { defaultValue: 'تصدير Excel للكل' })}
          </button>
          <button
            onClick={() => navigate('/audit-logs/statistics')}
            style={{
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaChartBar /> {t('auditLogs.statistics', { defaultValue: 'الإحصائيات' })}
          </button>
          <button
            onClick={() => navigate('/audit-logs/compare')}
            style={{
              backgroundColor: '#fd7e14',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaExchangeAlt /> {t('auditLogs.compare', { defaultValue: 'مقارنة' })}
          </button>
          {canCleanup && (
            <button
              onClick={() => setShowCleanupModal(true)}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaTrash /> {t('auditLogs.cleanup', { defaultValue: 'تنظيف السجلات' })}
            </button>
          )}
        </div>

        {showFilters && (
          <div style={{
            backgroundColor: '#1a1f2e',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            <div style={{ position: 'relative' }}>
              <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                {t('auditLogs.user', { defaultValue: 'المستخدم' })}
              </label>
              <div
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                style={{
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{getSelectedUser()}</span>
                <span>▼</span>
              </div>
              {userDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <input
                    ref={userSearchInputRef}
                    type="text"
                    placeholder={t('auditLogs.searchUser', { defaultValue: 'بحث...' })}
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#14263b',
                      border: 'none',
                      borderBottom: '1px solid #333',
                      color: 'white',
                      borderRadius: '6px 6px 0 0'
                    }}
                  />
                  {users.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => {
                        setUserId(String(user.id));
                        setUserDropdownOpen(false);
                      }}
                      style={{
                        padding: '10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #333',
                        color: String(userId) === String(user.id) ? '#AC2000' : 'white',
                        backgroundColor: String(userId) === String(user.id) ? '#2a2a2a' : 'transparent'
                      }}
                    >
                      {user.name || user.email || `User #${user.id}`}
                    </div>
                  ))}
                  {userPagination && userPagination.last_page > 1 && (
                    <div style={{ padding: '10px', borderTop: '1px solid #333' }}>
                      <button
                        onClick={() => setUserPage(prev => Math.max(1, prev - 1))}
                        disabled={userPage === 1}
                        style={{ marginRight: '10px', padding: '5px 10px' }}
                      >
                        ‹
                      </button>
                      <span style={{ color: '#ccc' }}>
                        {userPage} / {userPagination.last_page}
                      </span>
                      <button
                        onClick={() => setUserPage(prev => Math.min(userPagination.last_page, prev + 1))}
                        disabled={userPage === userPagination.last_page}
                        style={{ marginLeft: '10px', padding: '5px 10px' }}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                {t('auditLogs.modelType', { defaultValue: 'نوع النموذج' })}
              </label>
              <select
                value={modelType}
                onChange={(e) => setModelType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white'
                }}
              >
                <option value="">{t('auditLogs.all', { defaultValue: 'الكل' })}</option>
                {modelTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                {t('auditLogs.modelId', { defaultValue: 'معرف السجل' })}
              </label>
              <input
                type="text"
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                placeholder={t('auditLogs.modelIdPlaceholder', { defaultValue: 'معرف السجل' })}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                {t('auditLogs.event', { defaultValue: 'نوع الحدث' })}
              </label>
              <select
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white'
                }}
              >
                <option value="">{t('auditLogs.all', { defaultValue: 'الكل' })}</option>
                {eventTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                {t('auditLogs.fromDate', { defaultValue: 'من تاريخ' })}
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                {t('auditLogs.toDate', { defaultValue: 'إلى تاريخ' })}
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white'
                }}
              />
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                {t('auditLogs.perPage', { defaultValue: 'عدد السجلات' })}
              </label>
              <select
                value={perPage}
                onChange={(e) => setPerPage(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white'
                }}
              >
                <option value="15">15</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <button
                onClick={handleApplyFilters}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                {t('auditLogs.apply', { defaultValue: 'تطبيق' })}
              </button>
              <button
                onClick={handleResetFilters}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                {t('auditLogs.reset', { defaultValue: 'إعادة تعيين' })}
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ccc' }}>
            {t('auditLogs.loading', { defaultValue: 'جاري التحميل...' })}
          </div>
        ) : auditLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ccc' }}>
            {t('auditLogs.noData', { defaultValue: 'لا توجد بيانات' })}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', wordWrap: 'break-word' }}>
                <thead>
                  <tr style={{ backgroundColor: '#AC2000' }}>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.id', { defaultValue: 'ID' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.user', { defaultValue: 'المستخدم' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.auditableId', { defaultValue: 'معرف السجل' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.event', { defaultValue: 'الحدث' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.userAgent', { defaultValue: 'المتصفح' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.url', { defaultValue: 'الرابط' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.tags', { defaultValue: 'العلامات' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}
                    </th>
                    <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                      {t('auditLogs.actions', { defaultValue: 'الإجراءات' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #333' }}>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                        {log.id}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                        {log.user?.name || log.user?.email || (log.user_id ? `User #${log.user_id}` : '—')}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                        {getModelTypeLabel(log.auditable_type || log.model_type)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                        {log.auditable_id || log.model_id || '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                        {getEventLabel(log.event)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333', fontSize: '11px' }}>
                        {log.ip_address || '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'left', color: '#ddd', border: '1px solid #333', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px', title: log.user_agent || '—' }}>
                        {log.user_agent || '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'left', color: '#ddd', border: '1px solid #333', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '400px', title: log.url || '—' }}>
                        {log.url || '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                        {log.tags ? (Array.isArray(log.tags) ? log.tags.join(', ') : String(log.tags)) : '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                        {formatDate(log.created_at)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #333' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'nowrap' }}>
                          <ActionButton
                            title={t('auditLogs.previewPDF', { defaultValue: 'معاينة PDF' })}
                            bg="#17a2b8"
                            onClick={() => handlePreviewPDF(log)}
                          >
                            <FaEye />
                          </ActionButton>
                          <ActionButton
                            title={t('auditLogs.exportPDF', { defaultValue: 'تصدير PDF' })}
                            bg="#dc3545"
                            onClick={() => handleExportPDFSingle(log)}
                          >
                            <FaFilePdf />
                          </ActionButton>
                          <ActionButton
                            title={t('auditLogs.exportExcel', { defaultValue: 'تصدير Excel' })}
                            bg="#28a745"
                            onClick={() => handleExportExcelSingle(log)}
                          >
                            <FaFileExcel />
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.last_page > 1 && (
              <div style={{ marginTop: '20px', background: '#161c2c', border: '1px solid #2d3748', borderRadius: 10, padding: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={(pagination.current_page || 1) === 1}
                    style={paginationButtonStyle((pagination.current_page || 1) === 1)}
                  >
                    ««
                  </button>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={(pagination.current_page || 1) === 1}
                    style={paginationButtonStyle((pagination.current_page || 1) === 1)}
                  >
                    ‹
                  </button>
                  {pageNumbers.map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: page === (pagination.current_page || 1) ? '#AC2000' : '#1f2937',
                        color: 'white',
                        border: '1px solid #2d3748',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 12,
                        minWidth: 34,
                        fontWeight: page === (pagination.current_page || 1) ? 'bold' : 'normal',
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(pagination.last_page || 1, prev + 1))
                    }
                    disabled={(pagination.current_page || 1) >= (pagination.last_page || 1)}
                    style={paginationButtonStyle((pagination.current_page || 1) >= (pagination.last_page || 1))}
                  >
                    ›
                  </button>
                  <button
                    onClick={() => setCurrentPage(pagination.last_page || 1)}
                    disabled={(pagination.current_page || 1) >= (pagination.last_page || 1)}
                    style={paginationButtonStyle((pagination.current_page || 1) >= (pagination.last_page || 1))}
                  >
                    »»
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cleanup Confirmation Modal */}
      {showCleanupModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowCleanupModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '400px',
              maxWidth: '500px',
              border: '1px solid #333',
              direction: dir,
              textAlign
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'white', margin: 0, fontSize: '18px' }}>
                {t('auditLogs.confirmCleanupTitle', { defaultValue: 'تأكيد حذف السجلات' })}
              </h3>
              <button
                onClick={() => setShowCleanupModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#ccc',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('auditLogs.olderThanDays', { defaultValue: 'حذف السجلات الأقدم من (بالأيام)' })}
              </label>
              <input
                type="number"
                min="1"
                value={cleanupOlderThanDays}
                onChange={(e) => setCleanupOlderThanDays(parseInt(e.target.value) || 90)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>

            <p style={{ color: '#ccc', marginBottom: '20px', lineHeight: 1.6 }}>
              {t('auditLogs.confirmCleanupMessage', {
                defaultValue: `هل أنت متأكد من حذف جميع السجلات الأقدم من ${cleanupOlderThanDays} يوم؟`,
                days: cleanupOlderThanDays
              })}
            </p>
            <p style={{ color: '#ff6b6b', marginBottom: '20px', fontSize: '14px', fontWeight: 'bold' }}>
              {t('auditLogs.irreversibleAction', { defaultValue: 'هذا الإجراء لا يمكن التراجع عنه.' })}
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCleanupModal(false)}
                style={{
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('auditLogs.cancel', { defaultValue: 'إلغاء' })}
              </button>
              <button
                onClick={async () => {
                  if (!cleanupOlderThanDays || cleanupOlderThanDays < 1) {
                    toast.error(t('auditLogs.errors.invalidDays', { defaultValue: 'يرجى إدخال عدد أيام صحيح' }), { rtl: isRTL });
                    return;
                  }
                  setIsCleaning(true);
                  setShowCleanupModal(false);
                  try {
                    const result = await dispatch(cleanupAuditLogs({ olderThanDays: cleanupOlderThanDays, language: i18n.language }));
                    if (cleanupAuditLogs.fulfilled.match(result)) {
                      toast.success(
                        t('auditLogs.success.cleaned', {
                          defaultValue: 'تم حذف السجلات بنجاح',
                          count: result.payload?.deleted_count || 0
                        }),
                        { rtl: isRTL }
                      );
                      fetchAuditLogs(currentPage);
                    } else {
                      toast.error(result.payload || t('auditLogs.errors.cleanupError', { defaultValue: 'فشل في حذف السجلات' }), { rtl: isRTL });
                    }
                  } catch (error) {
                    toast.error(t('auditLogs.errors.cleanupError', { defaultValue: 'فشل في حذف السجلات' }), { rtl: isRTL });
                  } finally {
                    setIsCleaning(false);
                  }
                }}
                disabled={isCleaning}
                style={{
                  backgroundColor: isCleaning ? '#666' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: isCleaning ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FaTrash /> {t('auditLogs.confirm', { defaultValue: 'تأكيد الحذف' })}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PDF Preview Modal - Single Log */}
      {isPreviewOpen && previewLog && !previewLog.all && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            boxSizing: 'border-box',
            overflow: 'auto'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsPreviewOpen(false);
            }
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              display: 'flex',
              gap: '10px',
              zIndex: 1000000
            }}
          >
            <button
              onClick={() => {
                setPreviewZoom(prev => Math.min(2, prev + 0.1));
              }}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              +
            </button>
            <div style={{
              color: 'white',
              fontSize: '14px',
              minWidth: '50px',
              textAlign: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '8px 12px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {Math.round(previewZoom * 100)}%
            </div>
            <button
              onClick={() => {
                setPreviewZoom(prev => Math.max(0.1, prev - 0.1));
              }}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              −
            </button>
            <button
              onClick={() => {
                setPreviewZoom(1.0);
                setPreviewPosition({ x: 0, y: 0 });
              }}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {t('common.reset', { defaultValue: 'إعادة تعيين' })}
            </button>
            
            <button
              onClick={async () => {
                await handleExportPDFSingle({ id: previewLog.id });
              }}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaFilePdf /> {isGeneratingPdf ? t('auditLogs.generating', { defaultValue: 'جاري التوليد...' }) : t('auditLogs.printPDF', { defaultValue: 'طباعة PDF' })}
            </button>
            <button
              onClick={() => setIsPreviewOpen(false)}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              <FaTimes />
            </button>
          </div>

          <div
            id={`preview-audit-log-pdf-${previewLog.id}`}
            style={{
              transform: previewZoom > 1
                ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                : `scale(${previewZoom})`,
              transformOrigin: previewZoom > 1 ? 'center center' : 'top center',
              width: '100%',
              maxWidth: '794px',
              minHeight: '1123px',
              height: 'auto',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              margin: '80px auto 0',
              cursor: previewZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              transition: isDragging ? 'none' : 'transform 0.1s',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
            onMouseDown={(e) => {
              if (e.button === 0 && previewZoom > 1) {
                setIsDragging(true);
                setDragStart({
                  x: e.clientX - previewPosition.x,
                  y: e.clientY - previewPosition.y
                });
              }
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '1043px',
              height: 'auto',
              overflow: 'visible'
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                direction: dir,
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      marginBottom: '8px',
                      borderBottom: '4px double #000',
                      paddingBottom: '8px',
                      flexShrink: 0
                    }}
                    crossOrigin="anonymous"
                  />

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{
                      marginTop: '12px',
                      border: '2px solid #000',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(255, 255, 255, 0.3)'
                    }}>
                      {[
                        { label: t('auditLogs.id', { defaultValue: 'ID' }), value: previewLog.id || '—' },
                        { label: t('auditLogs.user', { defaultValue: 'المستخدم' }), value: previewLog.user?.name || previewLog.user?.email || (previewLog.user_id ? `User #${previewLog.user_id}` : '—') },
                        { label: t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' }), value: getModelTypeLabel(previewLog.auditable_type || previewLog.model_type) },
                        { label: t('auditLogs.auditableId', { defaultValue: 'معرف السجل' }), value: previewLog.auditable_id || previewLog.model_id || '—' },
                        { label: t('auditLogs.event', { defaultValue: 'الحدث' }), value: getEventLabel(previewLog.event), accent: true },
                        { label: t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' }), value: previewLog.ip_address || '—' },
                        { label: t('auditLogs.userAgent', { defaultValue: 'المتصفح' }), value: previewLog.user_agent || '—' },
                        { label: t('auditLogs.url', { defaultValue: 'الرابط' }), value: previewLog.url || '—' },
                        { label: t('auditLogs.tags', { defaultValue: 'العلامات' }), value: previewLog.tags ? (Array.isArray(previewLog.tags) ? previewLog.tags.join(', ') : String(previewLog.tags)) : '—' },
                        { label: t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' }), value: formatDate(previewLog.created_at) || '—' }
                      ].filter(row => row.value && row.value !== '—').map((row) => {
                        const pdfRowStyles = getPdfRowStyles(isRTL);
                        return (
                          <div key={row.label} style={pdfRowStyles.row}>
                            <div style={{
                              ...pdfRowStyles.label,
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}>
                              {row.label}
                            </div>
                            <div style={{
                              ...pdfRowStyles.value,
                              fontWeight: row.accent ? '700' : '500',
                              color: row.accent ? '#0057c2' : '#000',
                              wordBreak: 'break-word'
                            }}>
                              {row.value}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {(previewLog.old_values || previewLog.new_values) && (
                      <div style={{ marginTop: '20px', border: '2px solid #000', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                        <div style={{ padding: '10px', backgroundColor: 'rgba(227, 242, 253, 0.4)', borderBottom: '1px solid #000', fontWeight: '700', fontSize: '14px', color: '#1976d2', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                          {t('auditLogs.changes', { defaultValue: 'التغييرات' })}
                        </div>
                        <div style={{ display: 'flex', gap: 0 }}>
                          {previewLog.old_values && (
                            <div style={{ flex: 1, borderLeft: '1px solid #000' }}>
                              <div style={{ padding: '10px', backgroundColor: 'rgba(227, 242, 253, 0.4)', borderBottom: '1px solid #000', fontWeight: '700', fontSize: '14px', color: '#ff6b6b', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                {t('auditLogs.oldValues', { defaultValue: 'القيم القديمة' })}
                              </div>
                              <div style={{ padding: '10px', fontSize: '12px', direction: dir, textAlign: isRTL ? 'right' : 'left', maxHeight: '280px', overflowY: 'auto' }}>
                                {Object.entries(previewLog.old_values).map(([key, value]) => (
                                  <div key={key} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #ddd', lineHeight: '1.3', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                    <div style={{ fontWeight: '700', color: '#ff6b6b', marginBottom: '2px', fontSize: '11px', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>{translateFieldKey(key)}:</div>
                                    <div style={{ color: '#000', fontSize: '11px', wordBreak: 'break-word', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                      {value !== null && value !== undefined
                                        ? (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
                                        : '—'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {previewLog.new_values && (
                            <div style={{ flex: 1 }}>
                              <div style={{ padding: '10px', backgroundColor: 'rgba(227, 242, 253, 0.4)', borderBottom: '1px solid #000', fontWeight: '700', fontSize: '14px', color: '#51cf66', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                {t('auditLogs.newValues', { defaultValue: 'القيم الجديدة' })}
                              </div>
                              <div style={{ padding: '10px', fontSize: '12px', direction: dir, textAlign: isRTL ? 'right' : 'left', maxHeight: '280px', overflowY: 'auto' }}>
                                {Object.entries(previewLog.new_values).map(([key, value]) => (
                                  <div key={key} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #ddd', lineHeight: '1.3', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                    <div style={{ fontWeight: '700', color: '#51cf66', marginBottom: '2px', fontSize: '11px', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>{translateFieldKey(key)}:</div>
                                    <div style={{ color: '#000', fontSize: '11px', wordBreak: 'break-word', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                      {value !== null && value !== undefined
                                        ? (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
                                        : '—'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '18px',
                    right: '18px',
                    flexShrink: 0
                  }}>
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                          borderTop: '4px double #000',
                          paddingTop: '8px',
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PDF Print Template for Single Log - Multiple Pages for Old Values and New Values */}
      {pdfType === 'single' && previewLog && !previewLog.all && (() => {
        const oldValuesEntries = Object.entries(previewLog.old_values || {});
        const newValuesEntries = Object.entries(previewLog.new_values || {});
        const firstPageOldItems = 4; // Number of old values to show on first page
        const firstPageNewItems = 5; // Number of new values to show on first page
        const continuationPageItems = 10;
        const pages = [];
        
        pages.push(0); // First page - shows basic info + first 4 old values + first 5 new values
        // Calculate pages for old values continuation (remaining old values after first 4)
        let currentPageIndex = 1;
        if (oldValuesEntries.length > firstPageOldItems) {
          const remainingItems = oldValuesEntries.length - firstPageOldItems;
          const continuationPages = Math.ceil(remainingItems / continuationPageItems);
          for (let i = 0; i < continuationPages; i++) {
            pages.push(currentPageIndex + i);
          }
          currentPageIndex += continuationPages;
        }
        // Calculate pages for new values continuation (remaining new values after first 5)
        if (newValuesEntries.length > firstPageNewItems) {
          const remainingItems = newValuesEntries.length - firstPageNewItems;
          const continuationPages = Math.ceil(remainingItems / continuationPageItems);
          for (let i = 0; i < continuationPages; i++) {
            pages.push(currentPageIndex + i);
          }
        }
        
        return pages.map((pageIndex) => (
          <div
            key={pageIndex}
            id={pageIndex === 0 ? `audit-log-pdf-single-${previewLog.id}` : `audit-log-pdf-single-${previewLog.id}-page-${pageIndex}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '794px',
              height: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              overflow: 'visible'
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              height: '1043px',
              overflow: 'visible'
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
              padding: '18px 18px 10px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
              direction: dir,
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              overflow: 'visible'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: '95%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }}
                  crossOrigin="anonymous"
                />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  {pageIndex === 0 ? (
                    // First page - show audit log info and first 4 old values
                    <>
                      <div style={{
                        marginTop: '12px',
                        border: '2px solid #000',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: 'bold',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)'
                      }}>
                        {[
                          { label: t('auditLogs.id', { defaultValue: 'ID' }), value: previewLog.id || '—' },
                          { label: t('auditLogs.user', { defaultValue: 'المستخدم' }), value: previewLog.user?.name || previewLog.user?.email || (previewLog.user_id ? `User #${previewLog.user_id}` : '—') },
                          { label: t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' }), value: (previewLog.auditable_type || previewLog.model_type || '—').replace(/App\\Models\\/g, '') },
                          { label: t('auditLogs.auditableId', { defaultValue: 'معرف السجل' }), value: previewLog.auditable_id || previewLog.model_id || '—' },
                          { label: t('auditLogs.event', { defaultValue: 'الحدث' }), value: getEventLabel(previewLog.event), accent: true },
                          { label: t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' }), value: previewLog.ip_address || '—' },
                          { label: t('auditLogs.userAgent', { defaultValue: 'المتصفح' }), value: previewLog.user_agent || '—' },
                          { label: t('auditLogs.url', { defaultValue: 'الرابط' }), value: previewLog.url || '—' },
                          { label: t('auditLogs.tags', { defaultValue: 'العلامات' }), value: previewLog.tags ? (Array.isArray(previewLog.tags) ? previewLog.tags.join(', ') : String(previewLog.tags)) : '—' },
                          { label: t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' }), value: formatDate(previewLog.created_at) || '—' }
                        ].filter(row => row.value && row.value !== '—').map((row) => (
                          <div key={row.label} style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                            <div style={{
                              width: '30%',
                              borderLeft: '1px solid #000',
                              backgroundColor: 'rgba(227, 242, 253, 0.4)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: isRTL ? 'flex-end' : 'flex-start',
                              textAlign: isRTL ? 'right' : 'left',
                              fontWeight: '700',
                              fontSize: '14px',
                              direction: dir,
                              color: '#1976d2',
                              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: 'bold',
                              padding: '0 10px'
                            }}>
                              {row.label}
                            </div>
                            <div style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: isRTL ? 'flex-end' : 'flex-start',
                              textAlign: isRTL ? 'right' : 'left',
                              fontSize: '15px',
                              fontWeight: row.accent ? '700' : '500',
                              color: row.accent ? '#0057c2' : '#000',
                              direction: dir,
                              padding: '0 10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                              fontWeight: 'bold',
                              wordBreak: 'break-word'
                            }}>
                              {row.value}
                            </div>
                          </div>
                        ))}
                      </div>
                      {(oldValuesEntries.length > 0 || newValuesEntries.length > 0) && (
                        <div style={{ marginTop: '20px', border: '2px solid #000', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                          <div style={{ padding: '10px', backgroundColor: 'rgba(227, 242, 253, 0.4)', borderBottom: '1px solid #000', fontWeight: '700', fontSize: '14px', color: '#1976d2', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                            {t('auditLogs.changes', { defaultValue: 'التغييرات' })}
                          </div>
                          <div style={{ display: 'flex', gap: 0 }}>
                            {oldValuesEntries.length > 0 && (
                              <div style={{ flex: 1, borderLeft: '1px solid #000' }}>
                                <div style={{ padding: '10px', backgroundColor: 'rgba(227, 242, 253, 0.4)', borderBottom: '1px solid #000', fontWeight: '700', fontSize: '14px', color: '#ff6b6b', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                  {t('auditLogs.oldValues', { defaultValue: 'القيم القديمة' })}
                                </div>
                                <div style={{ padding: '10px', fontSize: '12px', direction: dir, textAlign: isRTL ? 'right' : 'left', maxHeight: '280px', overflowY: 'auto' }}>
                                  {oldValuesEntries.slice(0, firstPageOldItems).map(([key, value]) => (
                                    <div key={key} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #ddd', lineHeight: '1.3', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                      <div style={{ fontWeight: '700', color: '#ff6b6b', marginBottom: '2px', fontSize: '11px', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>{translateFieldKey(key)}:</div>
                                      <div style={{ color: '#000', fontSize: '11px', wordBreak: 'break-word', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                        {value !== null && value !== undefined
                                          ? (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
                                          : '—'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {newValuesEntries.length > 0 && (
                              <div style={{ flex: 1 }}>
                                <div style={{ padding: '10px', backgroundColor: 'rgba(227, 242, 253, 0.4)', borderBottom: '1px solid #000', fontWeight: '700', fontSize: '14px', color: '#51cf66', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                  {t('auditLogs.newValues', { defaultValue: 'القيم الجديدة' })}
                                </div>
                                <div style={{ padding: '10px', fontSize: '12px', direction: dir, textAlign: isRTL ? 'right' : 'left', maxHeight: '280px', overflowY: 'auto' }}>
                                  {newValuesEntries.slice(0, firstPageNewItems).map(([key, value]) => (
                                    <div key={key} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #ddd', lineHeight: '1.3', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                      <div style={{ fontWeight: '700', color: '#51cf66', marginBottom: '2px', fontSize: '11px', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>{translateFieldKey(key)}:</div>
                                      <div style={{ color: '#000', fontSize: '11px', wordBreak: 'break-word', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                        {value !== null && value !== undefined
                                          ? (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
                                          : '—'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    // Continuation pages - show remaining old values or new values
                    (() => {
                      const oldValuesContinuationPages = oldValuesEntries.length > firstPageOldItems 
                        ? Math.ceil((oldValuesEntries.length - firstPageOldItems) / continuationPageItems) 
                        : 0;
                      const isNewValuesPage = pageIndex > oldValuesContinuationPages;
                      
                      if (isNewValuesPage) {
                        // Show remaining new values
                        const newValuesPageIndex = pageIndex - oldValuesContinuationPages - 1;
                        const startIndex = firstPageNewItems + (newValuesPageIndex * continuationPageItems);
                        const endIndex = startIndex + continuationPageItems;
                        const pageNewValues = newValuesEntries.slice(startIndex, endIndex);
                        
                        return (
                          <div style={{ marginTop: '12px', border: '2px solid #000', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                            <div style={{ padding: '10px', backgroundColor: 'rgba(227, 242, 253, 0.4)', borderBottom: '1px solid #000', fontWeight: '700', fontSize: '14px', color: '#51cf66', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                              {t('auditLogs.newValues', { defaultValue: 'القيم الجديدة' })} ({startIndex + 1} - {Math.min(endIndex, newValuesEntries.length)})
                            </div>
                            <div style={{ padding: '10px', fontSize: '12px', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                              {pageNewValues.map(([key, value]) => (
                                <div key={key} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #ddd', lineHeight: '1.3', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                  <div style={{ fontWeight: '700', color: '#51cf66', marginBottom: '2px', fontSize: '11px', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>{translateFieldKey(key)}:</div>
                                  <div style={{ color: '#000', fontSize: '11px', wordBreak: 'break-word', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                    {value !== null && value !== undefined
                                      ? (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
                                      : '—'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      } else {
                        // Show remaining old values
                        const oldValuesPageIndex = pageIndex - 1;
                        const startIndex = firstPageOldItems + (oldValuesPageIndex * continuationPageItems);
                        const endIndex = startIndex + continuationPageItems;
                        
                        return (
                          <div style={{ marginTop: '12px', border: '2px solid #000', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                            <div style={{ padding: '10px', backgroundColor: 'rgba(227, 242, 253, 0.4)', borderBottom: '1px solid #000', fontWeight: '700', fontSize: '14px', color: '#ff6b6b', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                              {t('auditLogs.oldValues', { defaultValue: 'القيم القديمة' })} ({startIndex + 1} - {Math.min(endIndex, oldValuesEntries.length)})
                            </div>
                            <div style={{ padding: '10px', fontSize: '12px', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                              {oldValuesEntries.slice(startIndex, endIndex).map(([key, value]) => (
                                <div key={key} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #ddd', lineHeight: '1.3', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                  <div style={{ fontWeight: '700', color: '#ff6b6b', marginBottom: '2px', fontSize: '11px', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>{translateFieldKey(key)}:</div>
                                  <div style={{ color: '#000', fontSize: '11px', wordBreak: 'break-word', direction: dir, textAlign: isRTL ? 'right' : 'left' }}>
                                    {value !== null && value !== undefined
                                      ? (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))
                                      : '—'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    })()
                  )}
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '18px',
                  right: '18px',
                  flexShrink: 0
                }}>
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                        borderTop: '4px double #000',
                        paddingTop: '8px',
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        ));
      })()}

      {/* PDF Print Template for All Logs */}
      {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && (
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`audit-logs-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '794px',
              height: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              overflow: 'visible'
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              height: '1043px',
              overflow: 'visible'
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                direction: dir,
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      marginBottom: '8px',
                      borderBottom: '4px double #000',
                      paddingBottom: '8px',
                      flexShrink: 0
                    }}
                    crossOrigin="anonymous"
                  />

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '8px', textAlign: 'center', direction: dir }}>
                      <h2 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: '#000080', fontFamily: '"Times New Roman Bold", "Times New Roman", serif' }}>
                        {t('auditLogs.reportTitle')}
                      </h2>
                      <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#333', fontFamily: '"Times New Roman", serif' }}>
                        {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                      {(userId || modelType || event || fromDate || toDate) && (
                        <p style={{ margin: '0 0 4px 0', fontSize: '10px', color: '#555', fontFamily: '"Times New Roman", serif' }}>
                          {[
                            userId && `${t('auditLogs.user')}: #${userId}`,
                            modelType && `${t('auditLogs.model')}: ${getModelTypeLabel(modelType)}`,
                            event && `${t('auditLogs.event')}: ${getEventLabel(event)}`,
                            fromDate && `${t('auditLogs.from')}: ${fromDate}`,
                            toDate && `${t('auditLogs.to')}: ${toDate}`,
                          ].filter(Boolean).join(' | ')}
                        </p>
                      )}
                    </div>
                    <div style={{ marginTop: '6px', direction: dir, fontFamily: '"Times New Roman", "Times", serif' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)', width: '36px' }}>#</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.user', { defaultValue: 'المستخدم' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.auditableId', { defaultValue: 'معرف السجل' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.event', { defaultValue: 'الحدث' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chunk.map((log, idx) => {
                            const serialNumber = chunkIndex * AUDIT_LOGS_PDF_ITEMS_PER_PAGE + idx + 1;
                            return (
                              <tr key={log.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', width: '36px' }}>{serialNumber}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{log.user?.name || log.user?.email || (log.user_id ? `User #${log.user_id}` : '—')}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{getModelTypeLabel(log.auditable_type || log.model_type)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{log.auditable_id || log.model_id || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{getEventLabel(log.event)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{log.ip_address || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{formatDate(log.created_at)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '18px',
                    right: '18px',
                    flexShrink: 0
                  }}>
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                          borderTop: '4px double #000',
                          paddingTop: '8px',
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Preview Template for All Logs */}
      {isPreviewOpen && previewLog && previewLog.all && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            boxSizing: 'border-box',
            overflow: 'auto'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsPreviewOpen(false);
            }
          }}
        >
          <div
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              display: 'flex',
              gap: '10px',
              zIndex: 1000000
            }}
          >
            <button
              onClick={() => {
                setPreviewZoom(prev => Math.min(2, prev + 0.1));
              }}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              +
            </button>
            <div style={{
              color: 'white',
              fontSize: '14px',
              minWidth: '50px',
              textAlign: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '8px 12px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {Math.round(previewZoom * 100)}%
            </div>
            <button
              onClick={() => {
                setPreviewZoom(prev => Math.max(0.1, prev - 0.1));
              }}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              −
            </button>
            <button
              onClick={() => {
                setPreviewZoom(1.0);
                setPreviewPosition({ x: 0, y: 0 });
              }}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {t('common.reset', { defaultValue: 'إعادة تعيين' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaFilePdf /> {t('auditLogs.printPDF', { defaultValue: 'طباعة PDF' })}
            </button>
            <button
              onClick={() => setIsPreviewOpen(false)}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              <FaTimes />
            </button>
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '80px 20px 20px',
              minHeight: 'calc(100vh - 40px)',
              boxSizing: 'border-box',
              overflowX: 'auto'
            }}
          >
            <div
              style={{
                transform: previewZoom > 1 
                  ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                  : `scale(${previewZoom})`,
                transformOrigin: previewZoom > 1 ? 'center center' : 'top center',
                width: '100%',
                maxWidth: '794px',
                height: 'auto',
                cursor: previewZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                transition: isDragging ? 'none' : 'transform 0.1s',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                margin: '0 auto'
              }}
              onMouseDown={(e) => {
                if (e.button === 0 && previewZoom > 1) {
                  setIsDragging(true);
                  setDragStart({
                    x: e.clientX - previewPosition.x,
                    y: e.clientY - previewPosition.y
                  });
                }
              }}
            >
            {previewLog.chunks && previewLog.chunks.length > 0 ? (
              previewLog.chunks.map((chunk, chunkIndex) => (
                <div key={chunkIndex} id={`preview-audit-logs-pdf-all-${chunkIndex}`} style={{ marginBottom: chunkIndex < previewLog.chunks.length - 1 ? '20px' : '0' }}>
                  <div style={{
                    border: '5px solid #000',
                    padding: '3px',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box',
                    width: '794px',
                    minHeight: '1043px',
                    height: 'auto',
                    overflow: 'visible'
                  }}>
                    <div style={{
                      border: '2px solid #000',
                      minHeight: '1033px',
                      height: '1033px',
                      padding: '18px 18px 10px',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: 'bold',
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                      textRendering: 'optimizeLegibility',
                      direction: dir,
                      backgroundColor: '#ffffff',
                      boxSizing: 'border-box',
                      width: '100%',
                      overflow: 'visible'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${headerLogo})`,
                        backgroundSize: '95%',
                        backgroundPosition: 'center center',
                        backgroundRepeat: 'no-repeat',
                        opacity: 0.08,
                        zIndex: 0,
                        pointerEvents: 'none'
                      }} />
                      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                        <img
                          src={`${publicUrl}/HeaderAce.png`}
                          alt="Header"
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            marginBottom: '8px',
                            borderBottom: '4px double #000',
                            paddingBottom: '8px',
                            flexShrink: 0
                          }}
                          crossOrigin="anonymous"
                        />

                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                          <div style={{ marginTop: '8px', textAlign: 'center', direction: dir }}>
                            <h2 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: '#000080', fontFamily: '"Times New Roman Bold", "Times New Roman", serif' }}>
                              {t('auditLogs.reportTitle')}
                            </h2>
                            <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#333', fontFamily: '"Times New Roman", serif' }}>
                              {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                            {previewLog.filters && Object.values(previewLog.filters).some(Boolean) && (
                              <p style={{ margin: '0 0 4px 0', fontSize: '10px', color: '#555', fontFamily: '"Times New Roman", serif' }}>
                                {[
                                  previewLog.filters.userId && `${t('auditLogs.user')}: #${previewLog.filters.userId}`,
                                  previewLog.filters.modelType && `${t('auditLogs.model')}: ${getModelTypeLabel(previewLog.filters.modelType)}`,
                                  previewLog.filters.event && `${t('auditLogs.event')}: ${getEventLabel(previewLog.filters.event)}`,
                                  previewLog.filters.fromDate && `${t('auditLogs.from')}: ${previewLog.filters.fromDate}`,
                                  previewLog.filters.toDate && `${isRTL ? 'إلى' : 'To'}: ${previewLog.filters.toDate}`,
                                ].filter(Boolean).join(' | ')}
                              </p>
                            )}
                          </div>
                          <div style={{ marginTop: '6px', direction: dir, fontFamily: '"Times New Roman", "Times", serif' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                              <thead>
                                <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)', width: '36px' }}>#</th>
                                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.user', { defaultValue: 'المستخدم' })}</th>
                                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' })}</th>
                                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.auditableId', { defaultValue: 'معرف السجل' })}</th>
                                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.event', { defaultValue: 'الحدث' })}</th>
                                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' })}</th>
                                  <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {chunk.map((log, idx) => {
                                  const globalIndex = chunkIndex * AUDIT_LOGS_PDF_ITEMS_PER_PAGE + idx + 1;
                                  return (
                                    <tr key={log.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', width: '36px' }}>{globalIndex}</td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{log.user?.name || log.user?.email || (log.user_id ? `User #${log.user_id}` : '—')}</td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{getModelTypeLabel(log.auditable_type || log.model_type)}</td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{log.auditable_id || log.model_id || '—'}</td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{getEventLabel(log.event)}</td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{log.ip_address || '—'}</td>
                                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{formatDate(log.created_at)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div style={{
                          position: 'absolute',
                          bottom: '10px',
                          left: '18px',
                          right: '18px',
                          flexShrink: 0
                        }}>
                          <img
                            src={`${publicUrl}/FooterAce.png`}
                            alt="Footer"
                            style={{
                              width: '100%',
                              height: 'auto',
                              objectFit: 'contain',
                                borderTop: '4px double #000',
                                paddingTop: '8px',
                            }}
                            crossOrigin="anonymous"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div id="preview-audit-logs-pdf-all" style={{
                border: '5px solid #000',
                padding: '3px',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '794px',
                minHeight: '1043px',
                height: 'auto',
                overflow: 'visible'
              }}>
                <div style={{
                  border: '2px solid #000',
                  minHeight: '1033px',
                  height: '1033px',
                  padding: '18px 18px 10px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: 'bold',
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale',
                  textRendering: 'optimizeLegibility',
                  direction: dir,
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
                  width: '100%',
                  overflow: 'visible'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: '95%',
                    backgroundPosition: 'center center',
                    backgroundRepeat: 'no-repeat',
                    opacity: 0.08,
                    zIndex: 0,
                    pointerEvents: 'none'
                  }} />
                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        marginBottom: '8px',
                        borderBottom: '4px double #000',
                        paddingBottom: '8px',
                        flexShrink: 0
                      }}
                      crossOrigin="anonymous"
                    />

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                      <div style={{ marginTop: '8px', textAlign: 'center', direction: dir }}>
                        <h2 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: '#000080', fontFamily: '"Times New Roman Bold", "Times New Roman", serif' }}>
                          {t('auditLogs.reportTitle')}
                        </h2>
                        <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#333', fontFamily: '"Times New Roman", serif' }}>
                          {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                      <div style={{ marginTop: '6px', direction: dir, fontFamily: '"Times New Roman", "Times", serif' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                          <thead>
                            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)', width: '36px' }}>#</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.user', { defaultValue: 'المستخدم' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.auditableType', { defaultValue: 'نوع النموذج' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.auditableId', { defaultValue: 'معرف السجل' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.event', { defaultValue: 'الحدث' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.ipAddress', { defaultValue: 'عنوان IP' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(previewLog.logs || []).map((log, idx) => (
                              <tr key={log.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', width: '36px' }}>{idx + 1}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{log.user?.name || log.user?.email || (log.user_id ? `User #${log.user_id}` : '—')}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{getModelTypeLabel(log.auditable_type || log.model_type)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{log.auditable_id || log.model_id || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{getEventLabel(log.event)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{log.ip_address || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>{formatDate(log.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '18px',
                      right: '18px',
                      flexShrink: 0
                    }}>
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                            borderTop: '4px double #000',
                            paddingTop: '8px',
                        }}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ActionButton component similar to ProgressBill
const ActionButton = ({ children, onClick, title, bg = '#2f3644', disabled = false, style = {} }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    style={{
      backgroundColor: disabled ? '#666' : bg,
      color: 'white',
      border: 'none',
      borderRadius: 6,
      padding: '8px 10px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 38,
      opacity: disabled ? 0.6 : 1,
      ...style
    }}
  >
    {children}
  </button>
);

export default AuditLogs;
